// src/components/Social/SocialHub.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SocialWeeklySpotlight from './SocialWeeklySpotlight';
import SocialFeed from './SocialFeed';
import { MapContainer, TileLayer, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from '../../i18n';

import './SocialHub.css';
import logo from '../../assets/img/theme/tzone-logo-450x200.png';
import API_BASE_URL from '../../config/api';

const API_URL = API_BASE_URL;
const HOST = (process.env.REACT_APP_HOST || API_URL).replace(/\/+$/, '');

const DEFAULT_ATHLETE_AVATAR = `${HOST}/uploads/athletes/default.jpg`;
const DEFAULT_TRAINER_AVATAR = `${HOST}/uploads/trainers/default.jpg`;

// Normaliza valores de localStorage: '', 'undefined', 'null' → null
const sanitizeStored = (raw) => {
  if (raw === undefined || raw === null) return null;
  const v = String(raw).trim();
  if (!v) return null;
  const lower = v.toLowerCase();
  if (lower === 'undefined' || lower === 'null') return null;
  return v;
};

// ✅ Validación simple de ObjectId (24 hex)
const isObjectId = (v) => /^[a-f\d]{24}$/i.test(String(v || '').trim());

// Helper para resolver bien la URL de la foto
// Normaliza nombres de deporte en español/ingles al mismo "grupo", para
// no mostrar duplicados como "Cycling · Ciclismo" (mismo deporte, dos
// idiomas distintos guardados en los datos).
const SPORT_SYNONYMS = {
  cycling: 'Cycling', ciclismo: 'Cycling', bike: 'Cycling', mtb: 'Cycling',
  running: 'Running', correr: 'Running', trote: 'Running',
  trail: 'Trail',
  swim: 'Swim', natacion: 'Swim', nadar: 'Swim',
  strength: 'Strength', fuerza: 'Strength', gym: 'Strength',
  triathlon: 'Triathlon', triatlon: 'Triathlon',
  walk: 'Walk', caminar: 'Walk',
};
function normalizeSports(list) {
  const seen = new Set();
  const out = [];
  for (const raw of list || []) {
    const key = String(raw || '').trim().toLowerCase();
    if (!key) continue;
    const canonical = SPORT_SYNONYMS[key] || (raw.charAt(0).toUpperCase() + raw.slice(1));
    const dedupeKey = SPORT_SYNONYMS[key] || canonical.toLowerCase();
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    out.push(canonical);
  }
  return out;
}

const resolvePhotoUrl = (raw, { scope } = {}) => {
  if (!raw) return '';

  const v = String(raw).trim();
  if (!v) return '';

  // data URI base64
  if (/^data:image\//.test(v)) return v;

  // absoluta
  if (/^https?:\/\//i.test(v)) return v;

  // ya viene con /uploads/...
  if (v.startsWith('/')) return `${HOST}${v}`;

  // sólo nombre de archivo
  if (scope === 'trainer') return `${HOST}/uploads/trainers/${v}`;
  if (scope === 'athlete') return `${HOST}/uploads/athletes/${v}`;

  return `${HOST}/${v}`;
};

function formatDuration(minutes) {
  const total = Math.round(Number(minutes) || 0);
  if (total <= 0) return '-';
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const SEMI_TO_DEG = 180 / 2147483648;
function toDegMaybe(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return NaN;
  if (Math.abs(n) <= 180) return n;
  if (Math.abs(n) <= 2147483648) return n * SEMI_TO_DEG;
  return n;
}
function isValidLatLon(lat, lon) {
  return Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
}

// Mini-mapa de la ultima actividad en la card de Descubrir (homologacion
// con la app: mismo patron ya usado en SocialFeed.jsx)
function LastActivityMiniMap({ fileId, userId }) {
  const [points, setPoints] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!fileId) { setPoints([]); return; }
    const token = localStorage.getItem('token') || '';
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    if (userId) headers['x-user-id'] = userId;
    fetch(`${API_URL}/api/fit/parsed-data/${encodeURIComponent(fileId)}`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive) return;
        const records = Array.isArray(data?.records) ? data.records : [];
        const coords = [];
        for (const r of records) {
          const lat = toDegMaybe(r?.position_lat ?? r?.lat ?? r?.latitude);
          const lon = toDegMaybe(r?.position_long ?? r?.position_lon ?? r?.lon ?? r?.lng ?? r?.longitude);
          if (isValidLatLon(lat, lon)) coords.push([lat, lon]);
        }
        setPoints(coords);
      })
      .catch(() => setPoints([]));
    return () => { alive = false; };
  }, [fileId, userId]);

  if (points.length < 2) return null;
  return (
    <div className="social-card-last-activity-map">
      <MapContainer center={points[0]} zoom={12} scrollWheelZoom={false} dragging={false} zoomControl={false} style={{ width: '100%', height: '100%' }}>
        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Polyline positions={points} pathOptions={{ color: '#1878d6', weight: 3 }} />
      </MapContainer>
    </div>
  );
}

// Campanita de notificaciones: un solo lugar para todo lo que antes se
// avisaba (o no se avisaba, en el caso de la web) por push - te sumaron
// a un encuentro de tu grupo, quedaste en lista de espera, te invitaron
// por correo y ya tienes cuenta, alguien se unio al encuentro que
// organizas. Las que traen "inviteId" muestran Aceptar/Rechazar ahi
// mismo, sin tener que ir a buscar un correo.
function NotificationBell({ onNavigateToEvent }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [respondingId, setRespondingId] = useState(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token') || '';
    const userId = localStorage.getItem('userId') || '';
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    if (userId) headers['x-user-id'] = userId;
    return headers;
  };

  const load = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/social/notifications?limit=25`, {
        headers: authHeaders(),
      });
      const data = await resp.json().catch(() => null);
      if (resp.ok && data) {
        setItems(Array.isArray(data.notifications) ? data.notifications : []);
        setUnreadCount(Number(data.unreadCount) || 0);
      }
    } catch {
      // silencioso: no bloquea el resto de la pantalla
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 45000);
    return () => clearInterval(interval);
  }, []);

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await fetch(`${API_URL}/api/social/notifications/${id}/read`, {
        method: 'POST',
        headers: authHeaders(),
      });
    } catch {
      // si falla, la proxima carga corrige el estado
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await fetch(`${API_URL}/api/social/notifications/read-all`, {
        method: 'POST',
        headers: authHeaders(),
      });
    } catch {
      // idem
    }
  };

  const respondInvite = async (item, action) => {
    // Dos tipos distintos de "responder": una invitacion por correo a
    // alguien que ya tiene cuenta (SocialInvite, usa inviteId) vs un
    // auto-join de miembro de grupo que todavia no confirma su
    // asistencia (usa eventId, participante ya existe en el encuentro).
    const isGroupInvite = item?.type === 'event_group_invite';
    const inviteId = item?.data?.inviteId;
    const eventId = item?.data?.eventId;

    if (!isGroupInvite && !inviteId) return;
    if (isGroupInvite && !eventId) return;

    const url = isGroupInvite
      ? `${API_URL}/api/social/events/${eventId}/participation/${action === 'accept' ? 'confirm' : 'decline'}`
      : `${API_URL}/api/social/events/invitations/${inviteId}/${action}`;

    try {
      setRespondingId(item.id);
      const resp = await fetch(url, { method: 'POST', headers: authHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || 'No se pudo responder la invitación.');

      setItems((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, requiresAction: false, read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - (item.read ? 0 : 1)));

      if (action === 'accept' && onNavigateToEvent && item?.data?.eventId) {
        onNavigateToEvent(item.data.eventId);
        setOpen(false);
      }
    } catch (err) {
      alert(err.message || 'No se pudo responder la invitación.');
    } finally {
      setRespondingId(null);
    }
  };

  const handleItemClick = (item) => {
    if (!item.read) markRead(item.id);
    if (!item.requiresAction && item?.data?.eventId && onNavigateToEvent) {
      onNavigateToEvent(item.data.eventId);
      setOpen(false);
    }
  };

  const formatRelative = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return 'ahora';
    if (diffMin < 60) return `hace ${diffMin} min`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `hace ${diffH} h`;
    return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="social-bell-wrap">
      <button
        type="button"
        className="social-bell-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notificaciones"
      >
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 3a5 5 0 00-5 5v3.2c0 .7-.24 1.38-.68 1.93L5 15h14l-1.32-1.87A3.2 3.2 0 0117 11.2V8a5 5 0 00-5-5z" />
          <path d="M9.5 18a2.5 2.5 0 005 0" />
        </svg>
        <span className="social-bell-label">Avisos</span>
        {unreadCount > 0 && (
          <span className="social-bell-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <>
          <div className="social-bell-backdrop" onClick={() => setOpen(false)} />
          <div className="social-bell-panel">
            <div className="social-bell-panel-head">
              <h3>Notificaciones</h3>
              {unreadCount > 0 && <span className="social-bell-count">{unreadCount} nuevas</span>}
            </div>
            <div className="social-bell-list">
              {items.length === 0 && (
                <div className="social-bell-empty">No tienes notificaciones todavía.</div>
              )}
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`social-bell-item${item.read ? '' : ' social-bell-item--unread'}`}
                  onClick={() => handleItemClick(item)}
                >
                  <div className="social-bell-item-body">
                    <div className="social-bell-item-msg">
                      <strong>{item.title}</strong>
                    </div>
                    <div className="social-bell-item-sub">{item.body}</div>
                    {item.requiresAction && (
                      <div className="social-bell-item-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="social-cta-primary"
                          disabled={respondingId === item.id}
                          onClick={() => respondInvite(item, 'accept')}
                        >
                          {item.type === 'event_group_invite' ? 'Confirmar' : 'Aceptar'}
                        </button>
                        <button
                          type="button"
                          className="social-cta-secondary"
                          disabled={respondingId === item.id}
                          onClick={() => respondInvite(item, 'decline')}
                        >
                          {item.type === 'event_group_invite' ? 'No voy' : 'Rechazar'}
                        </button>
                      </div>
                    )}
                    <div className="social-bell-item-meta">{formatRelative(item.createdAt)}</div>
                  </div>
                  {!item.read && <span className="social-bell-item-dot" />}
                </div>
              ))}
            </div>
            {items.some((n) => !n.read) && (
              <button type="button" className="social-bell-panel-foot" onClick={markAllRead}>
                Marcar todo como leído
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const SocialHub = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, setLanguage } = useTranslation();

  // ✅ solo queremos montar widgets pesados (WeeklySpotlight) en el “home” social
  const isSocialHome =
    location.pathname === '/social' || location.pathname === '/social/';

  const [userInfo, setUserInfo] = useState({
    id: null,
    name: '',
    role: '',
    photoUrl: '',
  });

  const [cards, setCards] = useState([]);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [cardsError, setCardsError] = useState('');
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // 🔔 Modal de match tipo Bumble
  const [matchModal, setMatchModal] = useState(null);

  // 🔔 total de mensajes no leídos (para badge en “Mensajes”)
  const [unreadTotal, setUnreadTotal] = useState(0);

  // ✅ IDs de usuarios con match activo (para NO volver a recomendarlos)
  const [activeMatchIds, setActiveMatchIds] = useState([]);
  const activeMatchSet = useMemo(
    () => new Set((activeMatchIds || []).map((x) => String(x))),
    [activeMatchIds]
  );

  // ✅ Contactos en común con el currentCard
  const [mutualInfo, setMutualInfo] = useState({
    count: 0,
    users: [],
    hasMore: false,
  });
  const [mutualLoading, setMutualLoading] = useState(false);
  const [mutualModalOpen, setMutualModalOpen] = useState(false);

  // ✅ Ultima actividad del atleta actual (homologacion con la app)
  const [lastActivity, setLastActivity] = useState(null);

  // ==========================
  //   Carga datos del usuario
  // ==========================
  useEffect(() => {
    const rawUserId = localStorage.getItem('userId');
    const rawUserID = localStorage.getItem('userID'); // por si acaso
    const storedUserId = sanitizeStored(rawUserId || rawUserID);

    const rawRole =
      localStorage.getItem('userRole') || localStorage.getItem('role');
    const storedRole = sanitizeStored(rawRole) || '';

    const rawName =
      localStorage.getItem('userName') || localStorage.getItem('username');
    const fallbackName = sanitizeStored(rawName) || '';

    // Limpieza de basura previa en localStorage
    if (!storedUserId && (rawUserId || rawUserID)) {
      localStorage.removeItem('userId');
      localStorage.removeItem('userID');
    }
    if (!storedRole && rawRole) {
      localStorage.removeItem('userRole');
      localStorage.removeItem('role');
    }

    // ✅ Si el userId guardado NO es ObjectId, no lo enviamos al backend
    if (storedUserId && !isObjectId(storedUserId)) {
      console.warn('[SocialHub] userId inválido en localStorage:', storedUserId);
      localStorage.removeItem('userId');
      localStorage.removeItem('userID');
      navigate('/login');
      return;
    }

    if (!storedUserId || !storedRole) {
      setUserInfo({
        id: null,
        name: t('social.guest'),
        role: '',
        photoUrl: '',
      });
      return;
    }

    const loadProfile = async () => {
      try {
        // ===== ATLETA =====
        if (storedRole === 'athlete') {
          const res = await fetch(
            `${API_URL}/api/athletes/profile/${storedUserId}`
          );
          if (res.ok) {
            const data = await res.json();
            const a = data.athlete || {};

            const first = (a.firstName || '').trim();
            const last = (a.lastName || '').trim();

            const displayName =
              first || last
                ? `${first} ${last}`.trim()
                : (a.username || '').trim() || fallbackName || t('social.athleteFallback');

            const rawPhoto = a.profilePhoto || '/uploads/athletes/default.jpg';

            setUserInfo({
              id: storedUserId,
              name: displayName,
              role: 'athlete',
              photoUrl:
                resolvePhotoUrl(rawPhoto, { scope: 'athlete' }) ||
                DEFAULT_ATHLETE_AVATAR,
            });
            return;
          }
        }

        // ===== ENTRENADOR =====
        if (storedRole === 'trainer') {
          const res = await fetch(
            `${API_URL}/api/trainers/profile/${storedUserId}`
          );
          if (res.ok) {
            const data = await res.json();
            const trainerData = data.trainer || {};

            const first = (trainerData.firstName || '').trim();
            const last = (trainerData.lastName || '').trim();

            const displayName =
              first || last
                ? `${first} ${last}`.trim()
                : (trainerData.name || '').trim() ||
                  fallbackName ||
                  (trainerData.username || '').trim() ||
                  (trainerData.trainerName || '').trim() ||
                  t('social.trainerFallback');

            const rawPhoto =
              (trainerData.profilePhoto && trainerData.profilePhoto.trim()) ||
              '/uploads/trainers/default.jpg';

            const photoUrl = resolvePhotoUrl(rawPhoto, { scope: 'trainer' });

            setUserInfo({
              id: storedUserId,
              name: displayName,
              role: 'trainer',
              photoUrl: photoUrl || DEFAULT_TRAINER_AVATAR,
            });
            return;
          }
        }

        // Fallback si algo falla
        setUserInfo({
          id: storedUserId,
          name: fallbackName || t('social.tzoneUser'),
          role: storedRole,
          photoUrl: '',
        });
      } catch (err) {
        console.warn('[SocialHub] error cargando datos de usuario:', err);
        setUserInfo({
          id: storedUserId,
          name: fallbackName || t('social.tzoneUser'),
          role: storedRole,
          photoUrl: '',
        });
      }
    };

    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, t]);

  const roleLabel =
    userInfo.role === 'athlete'
      ? t('common.athlete')
      : userInfo.role === 'trainer'
      ? t('common.trainer')
      : '';

  // ==========================
  //   Carga total de no leídos
  //   + IDs de matches activos
  // ==========================
  useEffect(() => {
    if (!userInfo.id) return;

    const loadUnreadAndActiveMatches = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/community/matches?status=active`,
          {
            headers: {
              'Content-Type': 'application/json',
              'x-user-id': userInfo.id,
            },
          }
        );
        if (!res.ok) return;

        const raw = await res.json();
        const matches = Array.isArray(raw) ? raw : [];

        const total = matches.reduce((sum, m) => sum + (m.unreadCount || 0), 0);
        setUnreadTotal(total);

        const ids = [];
        for (const m of matches) {
          const ou = m.otherUser || {};
          const otherId = ou.id || ou.userId || ou._id || null;
          if (otherId) ids.push(String(otherId));
        }

        setActiveMatchIds(Array.from(new Set(ids)));
      } catch (err) {
        console.warn('[SocialHub] error cargando no leídos/matches', err);
      }
    };

    loadUnreadAndActiveMatches();
  }, [userInfo.id]);

  // ==========================
  //   Geolocalizacion real (opt-in) - homologacion con la app
  // ==========================
  // Pide el permiso de ubicacion al navegador (una vez por sesion) y lo
  // manda al backend para que /recommendations pueda ordenar "Descubrir"
  // por distancia real, igual que Tinder/Bumble. Si el usuario no da el
  // permiso, o el navegador no soporta geolocalizacion, esto simplemente
  // no hace nada - el discover sigue funcionando con el orden anterior.
  useEffect(() => {
    if (!userInfo.id) return;
    if (!('geolocation' in navigator)) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        fetch(`${API_URL}/api/community/location`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': userInfo.id,
          },
          body: JSON.stringify({ lat: latitude, lon: longitude }),
        }).catch((err) => {
          console.warn('[SocialHub] no se pudo guardar la ubicacion:', err);
        });
      },
      (err) => {
        // Permiso denegado o error de GPS: no es un problema, el
        // discover sigue funcionando sin distancia.
        console.warn('[SocialHub] geolocalizacion no disponible:', err?.message || err);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 }
    );
  }, [userInfo.id]);

  // ==========================
  //   Carga candidatos (cards)
  // ==========================
  useEffect(() => {
    if (!userInfo.id) return;

    const loadCandidates = async () => {
      setIsLoadingCards(true);
      setCardsError('');
      try {
        const res = await fetch(`${API_URL}/api/community/recommendations`, {
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': userInfo.id,
          },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        const mapped = (Array.isArray(data) ? data : [])
          .map((c) => {
            // ✅ Role estable (case-sensitive y con fallback)
            const role = (c.role || c.userRole || 'athlete').toString().trim() || 'athlete';
            const scope = role === 'trainer' ? 'trainer' : 'athlete';

            // ✅ userId estable (para filtros y llamadas)
            const userId = c.userId || c.user || c._id || c.id || null;

            // ✅ Foto primaria si existe
            const primaryPhoto =
              (Array.isArray(c.photos) && c.photos.find((p) => p?.isPrimary)?.url) ||
              (Array.isArray(c.photos) && c.photos[0]?.url) ||
              c.photoUrl ||
              c.avatarUrl ||
              c.profilePhoto ||
              '';

            const fallbackName =
              role === 'trainer' ? t('social.trainerFallback') : t('social.athleteFallback');

            const fallbackAvatar =
              role === 'trainer' ? DEFAULT_TRAINER_AVATAR : DEFAULT_ATHLETE_AVATAR;

            const photoUrlResolved = resolvePhotoUrl(primaryPhoto, { scope });

            return {
              id: c._id || c.id || c.userId || c.user,
              userId,
              displayName: (c.displayName || c.name || '').trim() || fallbackName,
              city: c.city || '',
              country: c.country || '',
              sports: normalizeSports(
                Array.isArray(c.sports) ? c.sports : c.sports ? [c.sports] : []
              ),
              goals: c.goals || c.goal || '',
              bio: c.bio || c.description || '',
              gender: c.gender || '',
              role,
              trainerStats: c.trainerStats || undefined,
              athleteStats: c.athleteStats || undefined,
              age: c.age || c.years || null,
              // ✅ Distancia real (geolocalizacion opt-in) - viene ya
              // calculada por el backend solo si YO y esta persona
              // dimos el permiso de ubicacion. Si falta, queda null y
              // simplemente no se muestra "a X km" en la card.
              distanceKm: Number.isFinite(c.distanceKm) ? c.distanceKm : null,
              photos: Array.isArray(c.photos)
                ? c.photos
                    .map((p) => ({
                      url: resolvePhotoUrl(p?.url || p, { scope }),
                      isPrimary: !!p?.isPrimary,
                    }))
                    .filter((p) => !!p.url)
                : [],
              // ✅ Si no viene foto, igual mostramos default según rol (evita cards “vacías”)
              photoUrl: photoUrlResolved || fallbackAvatar,
            };
          })
          .filter((card) => !!card.userId)
          .filter((card) => String(card.userId) !== String(userInfo.id))
          .filter((card) => !activeMatchSet.has(String(card.userId)));

        setCards(mapped);
        setCurrentPhotoIndex(0);
      } catch (err) {
        console.warn('[SocialHub] error cargando candidatos:', err);
        setCardsError(t('social.errors.loadRecommendations'));
      } finally {
        setIsLoadingCards(false);
      }
    };

    loadCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo.id, t]);

  // ✅ Si llegan/actualizan matches activos después, limpia el stack local
  useEffect(() => {
    if (!activeMatchIds || activeMatchIds.length === 0) return;
    setCards((prev) =>
      (prev || []).filter((c) => !activeMatchSet.has(String(c.userId)))
    );
  }, [activeMatchIds, activeMatchSet]);

  const currentCard = cards[0] || null;

  // ==========================
  //   Ultima actividad del atleta (homologacion con la app)
  // ==========================
  useEffect(() => {
    setLastActivity(null);
    console.log('[LA2]', { id: currentCard?.id, role: currentCard?.role, name: currentCard?.displayName });
    if (!currentCard?.id || currentCard.role === 'trainer') return;
    const headers = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('token') || '';
    if (token) headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    if (userInfo?.id) headers['x-user-id'] = userInfo.id;

    fetch(`${API_URL}/api/activities/user/${currentCard.id}?limit=1`, { headers })
      .then((r) => { console.log('[LA2] status', r.status); return r.ok ? r.json() : []; })
      .then((data) => {
        console.log('[LA2] data', data);
        if (Array.isArray(data) && data.length > 0) setLastActivity(data[0]);
      })
      .catch(() => {});
  }, [currentCard?.id]);

  // ==========================
  //   Carga "contactos en común"
  // ==========================
  useEffect(() => {
    const targetId = currentCard?.userId;
    setMutualModalOpen(false);

    if (!userInfo.id || !targetId) {
      setMutualInfo({ count: 0, users: [], hasMore: false });
      return;
    }

    const controller = new AbortController();

    const loadMutual = async () => {
      try {
        setMutualLoading(true);

        const res = await fetch(
          `${API_URL}/api/community/mutual-contacts?targetUserId=${encodeURIComponent(
            String(targetId)
          )}`,
          {
            signal: controller.signal,
            headers: {
              'Content-Type': 'application/json',
              'x-user-id': userInfo.id,
            },
          }
        );

        if (!res.ok) {
          setMutualInfo({ count: 0, users: [], hasMore: false });
          return;
        }

        const data = await res.json().catch(() => ({}));
        setMutualInfo({
          count: Number(data?.count || 0),
          users: Array.isArray(data?.users) ? data.users : [],
          hasMore: !!data?.hasMore,
        });
      } catch (e) {
        if (e?.name !== 'AbortError') {
          console.warn('[SocialHub] mutual-contacts error', e);
        }
        setMutualInfo({ count: 0, users: [], hasMore: false });
      } finally {
        setMutualLoading(false);
      }
    };

    loadMutual();
    return () => controller.abort();
  }, [userInfo.id, currentCard?.userId]);

  // ==========================
  //   Fotos
  // ==========================
  const photos = (() => {
    if (!currentCard) return [];
    if (Array.isArray(currentCard.photos) && currentCard.photos.length > 0) {
      return currentCard.photos.filter((p) => !!p.url);
    }
    if (currentCard.photoUrl) return [{ url: currentCard.photoUrl }];
    return [];
  })();

  const totalPhotos = photos.length;

  const currentPhotoUrl =
    (photos[currentPhotoIndex] && photos[currentPhotoIndex].url) ||
    photos[0]?.url ||
    '';

  const handleNextPhoto = () => {
    if (!totalPhotos) return;
    setCurrentPhotoIndex((idx) => (idx + 1) % totalPhotos);
  };

  // ==========================
  //   Like / Pass / Block / Report
  // ==========================
  const advanceCard = () => {
    setCards((prev) => prev.slice(1));
    setCurrentPhotoIndex(0);
    setMutualModalOpen(false);
  };

  const sendLike = async (likeValue) => {
    if (!userInfo.id || !currentCard || !currentCard.userId) return;

    try {
      const res = await fetch(`${API_URL}/api/community/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userInfo.id,
        },
        body: JSON.stringify({
          targetUserId: currentCard.userId,
          like: likeValue,
        }),
      });

      const data = await res.json().catch(() => null);

      if (likeValue && data && data.isMutual) {
        const match = data.match || {};

        setMatchModal({
          matchId: match._id || match.id || match.matchId || null,
          me: {
            name: userInfo.name,
            role: userInfo.role,
            photoUrl: userInfo.photoUrl,
          },
          partner: {
            userId: currentCard.userId,
            name: currentCard.displayName,
            role: currentCard.role,
            photoUrl:
              currentCard.photoUrl ||
              (currentCard.photos && currentCard.photos[0]?.url) ||
              '',
          },
        });

        // optimista: agregar inmediatamente al set activo
        setActiveMatchIds((prev) => {
          const p = Array.isArray(prev) ? prev : [];
          const next = new Set(p.map((x) => String(x)));
          next.add(String(currentCard.userId));
          return Array.from(next);
        });
      }
    } catch (err) {
      console.warn('[SocialHub] error enviando like/pass:', err);
    }
  };

  const handlePass = async (e) => {
    e.stopPropagation();
    await sendLike(false);
    advanceCard();
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    await sendLike(true);
    advanceCard();
  };

  const handleBlock = async (e) => {
    e.stopPropagation();
    if (!userInfo.id || !currentCard || !currentCard.userId) return;
    try {
      await fetch(`${API_URL}/api/community/block`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userInfo.id,
        },
        body: JSON.stringify({ targetUserId: currentCard.userId }),
      });
    } catch (err) {
      console.warn('[SocialHub] error bloqueando usuario:', err);
    }
    advanceCard();
  };

  const handleReport = async (e) => {
    e.stopPropagation();
    if (!userInfo.id || !currentCard || !currentCard.userId) return;
    try {
      await fetch(`${API_URL}/api/community/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userInfo.id,
        },
        body: JSON.stringify({
          targetUserId: currentCard.userId,
          reason: 'inappropriate',
          description: '',
        }),
      });
    } catch (err) {
      console.warn('[SocialHub] error reportando usuario:', err);
    }
    advanceCard();
  };

  // ==========================
  //   Acciones cabecera
  // ==========================
  const handleGoToDashboard = () => {
    if (userInfo.role === 'athlete') navigate('/athlete-dashboard');
    else if (userInfo.role === 'trainer') navigate('/trainer-dashboard');
    else navigate('/login');
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/social/profile');
  };

  const handleStartSearch = () => {
    console.log('[SocialHub] Buscar partner -> /social/search');
    navigate('/social/search');
  };

  const handleGoToMeetups = () => {
    navigate('/social/meetups');
  };

  const handleGoToBoard = () => {
    navigate('/social/meetups/board');
  };

  const handleGoToContacts = () => {
    navigate('/social/contacts');
  };

  const handleGoToMessages = () => {
    navigate('/social/messages');
  };

  // Helpers mutual
  const getMutualName = (u) =>
    (u?.name || u?.displayName || u?.username || '').trim() || t('social.contact');

  const getMutualAvatar = (u) => {
    const role = u?.role || u?.userRole || 'athlete';
    const scope = role === 'trainer' ? 'trainer' : 'athlete';

    const rawPhoto = u?.photoUrl || u?.avatarUrl || u?.profilePhoto || '';
    const fallback =
      scope === 'trainer' ? DEFAULT_TRAINER_AVATAR : DEFAULT_ATHLETE_AVATAR;

    return resolvePhotoUrl(rawPhoto, { scope }) || fallback;
  };

  // 👉 Ir a mensajes desde modal match (blindado)
  const goToMessages = () => {
    if (!matchModal) {
      handleGoToMessages();
      return;
    }

    const { matchId, me, partner } = matchModal;
    setMatchModal(null);

    // Si no hay matchId válido, igual entramos a mensajes sin “auto-open”
    const safeInitialMatchId = isObjectId(matchId) ? matchId : null;

    navigate('/social/messages', {
      state: {
        fromMatch: true,
        initialMatchId: safeInitialMatchId,
        matchContext: {
          me: {
            id: userInfo.id,
            name: me?.name || userInfo.name,
            photoUrl: me?.photoUrl || userInfo.photoUrl,
          },
          other: {
            id: partner?.userId || null,
            name: partner?.name || '',
            photoUrl: partner?.photoUrl || '',
          },
        },
      },
    });
  };

  const openMutualModal = (e) => {
    if (e) e.stopPropagation();
    if (mutualLoading) return;
    if (!mutualInfo || Number(mutualInfo.count || 0) <= 0) return;
    setMutualModalOpen(true);
  };

  return (
    <div className="social-root">
      {/* Barra superior tipo Bumble */}
      <header className="social-topbar">
        <div className="social-brand">
          <img src={logo} alt="Tzone" className="social-logo" />
          <span className="social-tagline">{t('social.tzoneCommunity')}</span>
        </div>

        <nav className="social-nav">
          <button
            className={`social-nav-item ${
              location.pathname.startsWith('/social/search')
                ? 'social-nav-item--active'
                : ''
            }`}
            type="button"
            onClick={handleStartSearch}
          >
            {t('social.searchPartner')}
          </button>

          <button
            className={`social-nav-item ${
              location.pathname.startsWith('/social/contacts')
                ? 'social-nav-item--active'
                : ''
            }`}
            type="button"
            onClick={handleGoToContacts}
          >
            {t('social.myContacts')}
          </button>

          <button
            className={`social-nav-item ${
              location.pathname.startsWith('/social/messages')
                ? 'social-nav-item--active'
                : ''
            }`}
            type="button"
            onClick={handleGoToMessages}
          >
            {t('social.messages')}
            {unreadTotal > 0 && (
              <span className="social-nav-unread-badge">
                {unreadTotal > 9 ? '9+' : unreadTotal}
              </span>
            )}
          </button>

          <button
            className={`social-nav-item ${
              location.pathname.startsWith('/social/meetups')
                ? 'social-nav-item--active'
                : ''
            }`}
            type="button"
            onClick={handleGoToMeetups}
          >
            {t('social.meetups')}
          </button>
        </nav>

        <div className="social-lang-toggle" role="group" aria-label="Idioma / Language">
          <button
            type="button"
            className={`social-lang-btn ${language === 'es' ? 'social-lang-btn--active' : ''}`}
            onClick={() => setLanguage('es')}
          >
            ES
          </button>
          <button
            type="button"
            className={`social-lang-btn ${language === 'en' ? 'social-lang-btn--active' : ''}`}
            onClick={() => setLanguage('en')}
          >
            EN
          </button>
        </div>

        <div className="social-user">
          {userInfo.photoUrl ? (
            <img
              src={userInfo.photoUrl}
              alt={userInfo.name}
              className="social-user-avatar"
            />
          ) : (
            <div className="social-user-avatar social-user-avatar--initials">
              {userInfo.name ? userInfo.name.charAt(0).toUpperCase() : '?'}
            </div>
          )}

          <div className="social-user-meta">
            <span className="social-user-name">
              {userInfo.name || t('social.myProfile')}
            </span>
            {roleLabel && <span className="social-user-role">{roleLabel}</span>}
          </div>

          <div className="social-user-actions">
            <NotificationBell
              onNavigateToEvent={(eventId) => navigate(`/social/meetups/${eventId}`)}
            />
            <button
              type="button"
              className="social-user-btn"
              onClick={handleGoToDashboard}
            >
              {t('common.dashboard')}
            </button>
            <button
              type="button"
              className="social-user-btn"
              onClick={handleGoToProfile}
            >
              {t('social.myCommunityProfile')}
            </button>
            <button
              type="button"
              className="social-user-btn social-user-btn--logout"
              onClick={handleLogout}
            >
              {t('social.logout')}
            </button>
          </div>
        </div>
      </header>

      <main className="social-main">
        <section className="social-hero">
          <h1>{t('social.findTrainingPartner')}</h1>
          <p>{t('social.heroDescription')}</p>

          <div className="social-hero-actions">
            <button
              type="button"
              className="social-cta-primary"
              onClick={handleGoToMeetups}
            >
              {t('social.organizeMeetup')}
            </button>
            <button
              type="button"
              className="social-cta-secondary"
              onClick={handleGoToBoard}
            >
              {t('social.viewMeetupsBoard')}
            </button>
          </div>

          <p className="social-hero-note">
            {t('social.heroNote')}
          </p>

          {/* ✅ IMPORTANTE: esto solo se monta en /social (evita fetch/logs en /social/search) */}
          {isSocialHome && (
            <section className="social-dashboard-section">
              <div className="social-dashboard-title" />
              <p className="social-dashboard-subtitle">
                {t('social.last7DaysResult')}
              </p>

              {userInfo?.role === 'athlete' && (
                <SocialWeeklySpotlight
                  userId={userInfo.id}
                  days={7}
                  onGoDashboard={handleGoToDashboard}
                />
              )}
            </section>
          )}

          {/* ✅ Feed: publicaciones de tus contactos (nuevo - homologacion con la app) */}
          {isSocialHome && userInfo?.id && (
            <section className="social-dashboard-section">
              <p className="social-dashboard-subtitle">Feed de tus contactos</p>
              <SocialFeed userId={userInfo.id} />
            </section>
          )}
        </section>

        <section className="social-cards">
          <div className="social-cards-header">
            <span className="social-cards-title">TZone Social</span>
            <span className="social-cards-count">{cards.length} disponibles</span>
          </div>
          <div className="social-card-stack">
            <div className="social-card-bg social-card-bg--left" />
            <div className="social-card-bg social-card-bg--right" />

            {currentCard && (
              <div
                className="social-card social-card--front"
                onClick={handleNextPhoto}
              >
                <div
                  className="social-card-photo"
                  style={{
                    backgroundImage: currentPhotoUrl
                      ? `url("${currentPhotoUrl}")`
                      : 'none',
                  }}
                />

                {totalPhotos > 0 && (
                  <div className="social-photo-dots">
                    {photos.map((_, idx) => (
                      <span
                        key={idx}
                        className={
                          'social-photo-dot' +
                          (idx === currentPhotoIndex
                            ? ' social-photo-dot--active'
                            : '')
                        }
                      />
                    ))}
                  </div>
                )}

                <div className="social-card-gradient" />

                <div className="social-card-photo-overlay">
                  <div className="social-card-name-row">
                    <span className="social-card-name">
                      {currentCard.displayName}
                      {currentCard.role !== 'trainer' && currentCard.age
                        ? `, ${currentCard.age}`
                        : ''}
                    </span>

                    <span className="social-card-role-badge">
                      {currentCard.role === 'trainer' ? 'Entrenador' : 'Atleta'}
                    </span>
                  </div>

                  {(currentCard.city || currentCard.country || currentCard.distanceKm != null) && (
                    <div className="social-card-overlay-location">
                      {currentCard.city && currentCard.country
                        ? `${currentCard.city}, ${currentCard.country}`
                        : currentCard.city || currentCard.country}
                      {currentCard.distanceKm != null && (
                        <span className="social-card-overlay-distance">
                          {(currentCard.city || currentCard.country) ? ' · ' : ''}
                          a {currentCard.distanceKm} km
                        </span>
                      )}
                    </div>
                  )}

                  {currentCard.sports?.length > 0 && (
                    <div className="social-card-overlay-chips">
                      {currentCard.sports.slice(0, 3).map((s) => (
                        <span key={s} className="social-card-overlay-chip">{s}</span>
                      ))}
                    </div>
                  )}

                  {(mutualLoading || mutualInfo.count > 0) && (
                    <div
                      className="social-mutual social-mutual--overlay"
                      role="button"
                      tabIndex={0}
                      onClick={openMutualModal}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openMutualModal(e);
                        }
                      }}
                      style={{
                        cursor: mutualInfo.count > 0 ? 'pointer' : 'default',
                      }}
                      title={
                        mutualLoading
                          ? t('social.loadingMutualContacts')
                          : mutualInfo.count > 0
                          ? t('social.viewMutualContacts')
                          : ''
                      }
                    >
                      <div className="social-mutual-avatars">
                        {(mutualInfo.users || []).slice(0, 3).map((u, idx) => {
                          const id = u.id || u.userId || u._id || `${idx}`;
                          const photo = getMutualAvatar(u);

                          return (
                            <img
                              key={String(id)}
                              src={photo}
                              alt={getMutualName(u)}
                              className="social-mutual-avatar"
                            />
                          );
                        })}
                      </div>

                      <div className="social-mutual-text">
                        {mutualLoading ? (
                          <span>{t('social.searchingMutualContacts')}</span>
                        ) : mutualInfo.count > 0 ? (
                          <>
                            <strong>{mutualInfo.count}</strong> contacto
                            {mutualInfo.count === 1 ? '' : 's'} en comun

                            {mutualInfo.hasMore ? (
                              <span
                                className="social-mutual-more"
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openMutualModal(e);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openMutualModal(e);
                                  }
                                }}
                                style={{ cursor: 'pointer' }}
                                title={t('social.viewAll')}
                              >
                                {' '}
                                · {t('social.viewAll')}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span />
                        )}
                      </div>
                    </div>
                  )}

                  <div className="social-card-actions social-card-actions--overlay">
                    <button
                      type="button"
                      className="social-btn-round social-btn-pass"
                      onClick={handlePass}
                    >
                      ✕
                    </button>

                    <button
                      type="button"
                      className="social-btn-round social-btn-like"
                      onClick={handleLike}
                    >
                      ❤️
                    </button>

                    <button
                      type="button"
                      className="social-btn-round-sm social-btn-block"
                      onClick={handleBlock}
                    >
                      🚫
                    </button>

                    <button
                      type="button"
                      className="social-btn-round-sm social-btn-report"
                      onClick={handleReport}
                    >
                      ⚠️
                    </button>
                  </div>
                </div>

                <div className="social-card-info">

                  {(mutualInfo.count > 0 || currentCard.sports?.length > 0) && (
                    <div className="social-mutual-summary">
                      {mutualInfo.count > 0 && (
                        <div className="social-mutual-summary-avatars">
                          {(mutualInfo.users || []).slice(0, 3).map((u, idx) => {
                            const id = u.id || u.userId || u._id || `${idx}`;
                            const photo = getMutualAvatar(u);
                            return (
                              <img
                                key={String(id)}
                                src={photo}
                                alt={getMutualName(u)}
                                className="social-mutual-summary-avatar"
                              />
                            );
                          })}
                        </div>
                      )}
                      <div className="social-mutual-summary-text">
                        <p className="social-mutual-summary-title">
                          {mutualInfo.count > 0
                            ? 'Tenemos contactos en comun'
                            : 'Tenemos deportes en comun'}
                        </p>
                        <div className="social-mutual-summary-chips">
                          {(currentCard.sports || []).slice(0, 2).map((s) => (
                            <span key={`common-${s}`}>{s}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {currentCard.bio && (
                    <div className="social-card-bio">{currentCard.bio}</div>
                  )}

                  {currentCard.role === 'trainer' && (
                    <div className="social-card-trainer-work">
                      <div className="social-card-trainer-work-header">
                        <div>
                          <p className="social-card-trainer-work-label">Trabajo del entrenador</p>
                          <p className="social-card-trainer-work-headline">
                            {currentCard.trainerStats?.headline || 'Planificacion y seguimiento'}
                          </p>
                        </div>
                        <div className="social-card-trainer-work-grid">
                          {[
                            [currentCard.trainerStats?.athleteCount ?? 0, 'Atletas'],
                            [
                              currentCard.trainerStats?.compliancePercent != null
                                ? `${currentCard.trainerStats.compliancePercent}%`
                                : '-',
                              'Cumpl.',
                            ],
                            [currentCard.trainerStats?.groupCount ?? 0, 'Grupos'],
                          ].map(([value, label]) => (
                            <div key={label}>
                              <b>{value}</b>
                              <span>{label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="social-card-trainer-work-tags">
                        {(
                          currentCard.trainerStats?.methodHighlights || [
                            'Disponible para nuevos atletas',
                            'Cumplimiento por atleta',
                            'Modelo Banister CTL / ATL / TSB',
                          ]
                        )
                          .slice(0, 4)
                          .map((item) => (
                            <span key={item}>{item}</span>
                          ))}
                      </div>

                      {(currentCard.trainerStats?.publicAthletes || []).length > 0 && (
                        <div className="social-card-trainer-athletes">
                          <div className="social-card-trainer-athletes-header">
                            <span>Atletas del entrenador</span>
                            <span>Top {Math.min(10, currentCard.trainerStats?.publicAthletes?.length || 0)}</span>
                          </div>
                          <div className="social-card-trainer-athletes-grid">
                            {(currentCard.trainerStats?.publicAthletes || []).slice(0, 4).map((athlete, index) => (
                              <div key={(athlete.name || 'athlete') + '-' + index} className="social-card-trainer-athlete">
                                <img
                                  src={resolvePhotoUrl(athlete.photoUrl || '', { scope: 'athlete' })}
                                  alt={athlete.name || 'Atleta'}
                                  className="social-card-trainer-athlete-avatar"
                                />
                                <span>{athlete.name || 'Atleta'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="social-card-trainer-banister">
                        <div className="social-card-trainer-banister-header">
                          <span>Top Banister</span>
                          <span>
                            {currentCard.trainerStats?.banisterPeriod === 'historical'
                              ? 'Historico'
                              : currentCard.trainerStats?.banisterPeriod === 'latest'
                              ? 'Ultimo dato'
                              : currentCard.trainerStats?.banisterPeriod || 'CTL / ATL / TSB'}
                          </span>
                        </div>
                        {(currentCard.trainerStats?.banisterTop || []).length > 0 ? (
                          (currentCard.trainerStats?.banisterTop || []).slice(0, 3).map((row, index) => (
                            <div key={(row.name || 'row') + '-' + index} className="social-card-trainer-banister-row">
                              <b>{index + 1}</b>
                              <span className="social-card-trainer-banister-name">
                                <span>{row.name || 'Atleta'}</span>
                                {row.periodLabel && <span className="social-card-trainer-banister-period">{row.periodLabel}</span>}
                              </span>
                              <span className="social-card-trainer-banister-values">
                                CTL {Number(row.ctl || 0).toFixed(1)} · ATL {Number(row.atl || 0).toFixed(1)} · TSB {Number(row.tsb || 0).toFixed(1)}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="social-card-trainer-banister-empty">
                            Top 10 disponible cuando existan datos CTL / ATL / TSB.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {currentCard.role !== 'trainer' && lastActivity && (
                    <div className="social-card-last-activity">
                      <div className="social-card-last-activity-header">
                        <span>Ultima actividad</span>
                        <span className="social-card-last-activity-type">
                          {lastActivity.type || lastActivity.title || 'Actividad'}
                        </span>
                      </div>
                      <LastActivityMiniMap fileId={lastActivity.completed?.fileId || lastActivity.fitFile} userId={userInfo.id} />
                      <div className="social-card-last-activity-grid">
                        <div>
                          <b>{Number(lastActivity.completed?.distance || 0) > 0 ? Number(lastActivity.completed.distance).toFixed(1) : '-'}</b>
                          <span>km</span>
                        </div>
                        <div>
                          <b>{lastActivity.completed?.duration?.slice(0, 5) || '-'}</b>
                          <span>h:m</span>
                        </div>
                        <div>
                          <b>{lastActivity.completed?.ritmo_medio || lastActivity.completed?.avgPace || '-'}</b>
                          <span>Ritmo</span>
                        </div>
                        <div>
                          <b>{Number(lastActivity.completed?.totalAscent || 0) > 0 ? `+${Math.round(lastActivity.completed.totalAscent)}` : '-'}</b>
                          <span>ELV</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {currentCard.role !== 'trainer' && currentCard.athleteStats && (
                    <div className="social-card-athlete-results">
                      <div className="social-card-athlete-results-header">
                        <div>
                          <p className="social-card-athlete-results-label">Resultados del atleta</p>
                          <p className="social-card-athlete-results-period">
                            {currentCard.athleteStats.periodLabel || 'Historico'}
                          </p>
                        </div>
                        <span className="social-card-athlete-results-badge">CTL / ATL / TSB</span>
                      </div>
                      <div className="social-card-athlete-results-grid">
                        {[
                          [Number(currentCard.athleteStats.distanceKm || 0).toFixed(1), 'km'],
                          [formatDuration(currentCard.athleteStats.durationMinutes), 'tiempo'],
                          ['-', 'ritmo'],
                          [currentCard.athleteStats.sessions || 0, 'sesiones'],
                        ].map(([value, label]) => (
                          <div key={label}>
                            <b>{value}</b>
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                      <div className="social-card-athlete-banister-row">
                        {[
                          [`CTL ${Number(currentCard.athleteStats.ctl || 0).toFixed(1)}`, 'fitness'],
                          [`ATL ${Number(currentCard.athleteStats.atl || 0).toFixed(1)}`, 'fatiga'],
                          [`TSB ${Number(currentCard.athleteStats.tsb || 0).toFixed(1)}`, 'forma'],
                        ].map(([value, label]) => (
                          <div key={label}>
                            <b>{value}</b>
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {currentCard.goals && (
                    <div className="social-card-goal">
                      {t('social.goal')}: {currentCard.goals}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!currentCard && !isLoadingCards && (
              <div className="social-empty">
                <p>{t('social.noMorePartners')}</p>
              </div>
            )}

            {isLoadingCards && (
              <div className="social-empty">
                <p>{t('social.loadingRecommendations')}</p>
              </div>
            )}

            {cardsError && (
              <div className="social-error">
                <p>{cardsError}</p>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Modal mutual */}
      {mutualModalOpen && currentCard && mutualInfo.count > 0 && (
        <div
          className="social-match-overlay"
          onClick={() => setMutualModalOpen(false)}
        >
          <div
            className="social-match-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="social-match-title">{t('social.mutualConnections')}</h2>

            <p className="social-match-subtitle">
              {t('social.mutualConnectionsDescription', {
                name: currentCard.displayName,
              })}
            </p>

            <div style={{ marginTop: 10 }}>
              {(mutualInfo.users || []).slice(0, 10).map((u, idx) => {
                const id = u.id || u.userId || u._id || `${idx}`;
                const name = getMutualName(u);
                const avatar = getMutualAvatar(u);

                return (
                  <div
                    key={String(id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 0',
                      borderBottom: '1px solid rgba(0,0,0,0.06)',
                    }}
                  >
                    <img
                      src={avatar}
                      alt={name}
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 999,
                        objectFit: 'cover',
                      }}
                    />
                    <div style={{ lineHeight: 1.25 }}>
                      <div style={{ fontWeight: 700 }}>{name}</div>
                      <div style={{ opacity: 0.85, fontSize: 13 }}>
                        {t('social.connectionPath', {
                          name,
                          currentCardName: currentCard.displayName,
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="social-match-actions">
              <button
                type="button"
                className="social-cta-secondary"
                onClick={() => setMutualModalOpen(false)}
              >
                {t('social.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal match */}
      {matchModal && (
        <div className="social-match-overlay">
          <div className="social-match-modal">
            <h2 className="social-match-title">{t('social.itsAMatch')}</h2>
            <p className="social-match-subtitle">
              {t('social.matchDescription.before')}{' '}
              <strong>{t('social.messages')}</strong>.{' '}
              {t('social.matchDescription.after')}
            </p>

            <div className="social-match-avatars">
              <div className="social-match-avatar">
                <img
                  src={
                    matchModal.me.photoUrl ||
                    (matchModal.me.role === 'trainer'
                      ? DEFAULT_TRAINER_AVATAR
                      : DEFAULT_ATHLETE_AVATAR)
                  }
                  alt={matchModal.me.name}
                />
              </div>
              <div className="social-match-avatar social-match-avatar--right">
                <img
                  src={
                    matchModal.partner.photoUrl ||
                    (matchModal.partner.role === 'trainer'
                      ? DEFAULT_TRAINER_AVATAR
                      : DEFAULT_ATHLETE_AVATAR)
                  }
                  alt={matchModal.partner.name}
                />
              </div>
            </div>

            <p className="social-match-names">
              {matchModal.me.name} <span>&amp;</span> {matchModal.partner.name}
            </p>

            <div className="social-match-actions">
              <button
                type="button"
                className="social-cta-primary"
                onClick={goToMessages}
              >
                {t('social.goToMessages')}
              </button>
              <button
                type="button"
                className="social-cta-secondary"
                onClick={() => setMatchModal(null)}
              >
                {t('social.keepSearching')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialHub;
